/**
 * Enhances every Shopify-emitted <model-viewer> on the storefront:
 *  - Tightens the auto camera so the device fills more of the gallery panel.
 *  - Replaces the white auto-generated poster with a branded dark poster that
 *    shows a throbbing GESKE logo while the GLB downloads.
 *  - Holds the GLB back until the page has loaded and gone idle (or the
 *    shopper touches the gallery), then loads it eagerly and auto-reveals.
 *
 * Idempotent and MutationObserver-driven so it covers model-viewers that
 * Horizon injects after initial render (carousel slide changes, deferred
 * media activation, gallery zoom dialog).
 */
(function () {
  if (window.__geskeModelViewerInit) return;
  window.__geskeModelViewerInit = true;

  // Resolve the studio environment image (hosted as a sibling theme asset) so
  // model-viewer lights devices like geske.com: warm reflections on the brass
  // and a deeper black body. Derived from this script's own /assets/ URL.
  var GESKE_SCRIPT = document.currentScript;
  var GESKE_ENV_URL = (function () {
    try {
      var s = GESKE_SCRIPT || document.querySelector('script[src*="geske-model-viewer"]');
      var src = s && s.src ? s.src : '';
      return src ? src.replace(/geske-model-viewer\.js(\?.*)?$/, 'geske-studio-env.jpg') : '';
    } catch (e) { return ''; }
  })();

  // Per-device exposure (model-viewer v1.12 / ACES crushes shadows, so most
  // devices need a high exposure to show their dark-body texture). B0703's
  // gold/bronze body blows out at high exposure, so it gets its own low value.
  // Keyed by the product code embedded in the model's GLB filename/URL.
  var GESKE_DEFAULT_EXPOSURE = '2.7';
  // B0062 (Lip Volumizer) is an all-black device with no per-material exposure lift in
  // .com's material config, so at the default it reads too dark vs .com; give it more.
  var GESKE_EXPOSURE_BY_CODE = { B0703: '1.0', B0062: '3.6' };

  // The loading poster shows the real GESKE mark.
  //
  // It previously drew an approximation of the logo with two SVG <text> nodes
  // in Georgia / Times New Roman and Arial. Those are system fonts, not the
  // brand faces, so for the second or so it takes the GLB to download every 3D
  // PDP flashed up a wordmark that was visibly not the GESKE logo - reported as
  // the "old logo flash". The paths below are the same vector artwork used by
  // the site header (sections/geske-header.liquid, viewBox 0 0 105 30), with
  // fill="currentColor" so the poster's own colour still applies.
  var POSTER_MARKUP =
    '<div class="geske-model-poster__inner">' +
      '<svg class="geske-model-poster__logo" viewBox="0 0 105 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<path fill="currentColor" d="M21.665 9.646H15.78v1.407h2.047v3.966c-.768.537-1.766.844-2.891.844-3.096 0-4.836-1.842-4.836-7.036 0-5.706 1.944-7.292 4.938-7.292 2.584 0 4.375 1.612 4.759 4.043h1.28C20.615 2.379 18.056 0 14.602 0c-4.887 0-8.52 3.684-8.52 8.827 0 5.168 2.66 8.571 8.34 8.571 1.715 0 3.25-.588 4.376-1.535.563.435.998.844.998 1.28h1.868V9.645ZM28.827 17.142h11.488v-1.407H32.69V9.39h6.576V7.983H32.69v-6.32h7.087V.256h-10.95v16.886ZM52.329 1.458c2.047 0 3.736.921 4.12 3.173h1.279C57.344 1.842 55.553 0 52.175 0c-3.71 0-6.038 2.252-6.038 5.092 0 2.763 1.51 4.042 4.631 5.347 2.508 1.049 3.71 1.51 3.71 3.121 0 1.612-1.484 2.38-3.147 2.38-2.482 0-4.145-1.126-4.554-3.812h-1.28c.205 3.428 2.764 5.27 6.218 5.27 3.607 0 6.268-1.893 6.268-4.912 0-2.584-1.33-3.966-5.628-5.655-2.226-.87-2.968-1.74-2.968-2.89 0-1.51 1.074-2.483 2.942-2.483ZM68.653.256h-3.864v16.886h3.864V.256Zm11.615 16.886L72.055 7.42 79.194.256h-2.2l-8.162 8.418 6.626 8.468h4.81ZM86.755 17.142h11.488v-1.407H90.62V9.39h6.575V7.983H90.62v-6.32h7.087V.256h-10.95v16.886Z"/>' +
        '<path fill="currentColor" d="M2.322 30c-.508 0-.934-.094-1.28-.282a1.835 1.835 0 0 1-.78-.8C.087 28.574 0 28.165 0 27.692c0-.478.094-.89.281-1.235.188-.35.457-.618.806-.806.354-.191.779-.287 1.273-.287.329 0 .616.044.864.134.247.085.465.211.652.377l-.275.64a2.535 2.535 0 0 0-.39-.256 1.421 1.421 0 0 0-.384-.134 2.11 2.11 0 0 0-.46-.045c-.495 0-.87.14-1.126.422-.252.277-.377.676-.377 1.196 0 .52.119.917.358 1.19.243.273.622.41 1.138.41.175 0 .35-.018.525-.052.179-.034.354-.083.524-.147l-.153.345v-1.35H2.2v-.6h1.74v2.168c-.209.106-.458.19-.748.25s-.58.089-.87.089ZM6.673 29.936v-4.51H9.71v.647H7.466v1.247h2.098v.652H7.466v1.311h2.245v.653H6.673ZM12.268 29.936v-4.51h1.97c.495 0 .876.118 1.145.352.273.235.41.561.41.979 0 .277-.062.514-.186.71-.12.196-.292.348-.518.454a1.925 1.925 0 0 1-.8.154l.09-.122h.217c.18 0 .34.047.48.14.14.095.256.238.345.43l.66 1.413h-.909l-.665-1.465a.484.484 0 0 0-.275-.294 1.192 1.192 0 0 0-.435-.07h-.704v1.829h-.825Zm.825-2.437h1.01c.29 0 .51-.06.66-.18.153-.123.23-.302.23-.537 0-.234-.077-.411-.23-.53-.15-.12-.37-.18-.66-.18h-1.01V27.5ZM18.557 29.936v-4.51h.722l1.657 3.73h-.198l1.657-3.73h.716v4.51h-.748v-3.345h.23l-1.516 3.345h-.493l-1.516-3.345h.237v3.345h-.748ZM25.408 29.936l2.04-4.51h.691l2.066 4.51h-.838l-.55-1.267.333.224h-2.706l.346-.224-.55 1.267h-.832Zm2.38-3.627-.902 2.137-.167-.199h2.15l-.135.199-.921-2.137h-.026ZM32.495 29.936v-4.51h.62l2.617 3.384h-.167v-3.383h.768v4.509h-.62l-2.61-3.384h.16v3.384h-.768ZM42.66 29.936v-4.51h1.977c.49 0 .868.105 1.133.314.264.205.396.49.396.857 0 .273-.08.503-.243.69a1.204 1.204 0 0 1-.672.378v-.096c.329.047.583.167.762.358.183.188.275.438.275.749 0 .4-.139.712-.416.934-.277.217-.663.326-1.158.326h-2.053Zm.794-.627h1.164c.277 0 .493-.051.646-.153.154-.107.23-.28.23-.518 0-.244-.076-.416-.23-.519-.153-.106-.369-.16-.646-.16h-1.164v1.35Zm0-1.97h1.043c.294 0 .511-.053.652-.16.145-.11.218-.273.218-.486 0-.213-.073-.373-.218-.48-.14-.11-.358-.166-.652-.166h-1.043v1.292ZM48.868 29.936v-4.51h3.038v.647h-2.245v1.247h2.098v.652h-2.098v1.311h2.245v.653h-3.038ZM53.883 29.936l2.04-4.51h.691l2.066 4.51h-.837l-.55-1.267.332.224h-2.706l.346-.224-.55 1.267h-.832Zm2.38-3.627-.902 2.137-.167-.199h2.15l-.135.199-.92-2.137h-.026ZM62.82 30c-.644 0-1.124-.16-1.44-.48-.31-.324-.467-.802-.467-1.433v-2.66h.826v2.654c0 .4.085.704.256.908.174.205.45.307.825.307.358 0 .627-.102.806-.307.183-.204.275-.507.275-.908v-2.654h.812v2.66c0 .631-.16 1.11-.48 1.433-.315.32-.787.48-1.413.48ZM68.527 29.936v-3.831h-1.49v-.678h3.805v.678h-1.49v3.831h-.825ZM74.397 29.936v-2.13l.173.492-1.938-2.871h.953l1.33 2.046h-.178l1.33-2.046h.921l-1.932 2.872.167-.493v2.13h-.826ZM83.72 29.936v-3.831h-1.491v-.678h3.806v.678h-1.49v3.831h-.826ZM88.409 29.936v-4.51h3.038v.647h-2.245v1.247H91.3v.652h-2.098v1.311h2.245v.653h-3.038ZM96.095 30c-.469 0-.872-.094-1.209-.282a1.943 1.943 0 0 1-.767-.805c-.18-.35-.269-.762-.269-1.235 0-.473.09-.883.269-1.228.18-.345.435-.612.767-.8.337-.191.74-.287 1.21-.287.315 0 .599.044.85.134.256.09.476.222.659.396l-.269.653a2.062 2.062 0 0 0-.582-.345 1.714 1.714 0 0 0-.633-.11c-.456 0-.806.14-1.049.416-.239.273-.358.664-.358 1.171 0 .508.12.9.358 1.177.243.277.593.416 1.049.416.239 0 .45-.034.633-.103.188-.072.382-.191.582-.358l.269.653a1.93 1.93 0 0 1-.659.396c-.251.094-.535.14-.85.14ZM100.167 29.936v-4.51h.825v1.881h2.309v-1.88h.826v4.509h-.826v-1.945h-2.309v1.945h-.825Z"/>' +
      '</svg>' +
    '</div>';

  // Hide Shopify's white square preview-image poster (#default-poster) inside
  // the model-viewer shadow root so only the branded logo poster shows during
  // load. #default-poster carries no ::part hook, so it can't be reached from
  // the light-DOM stylesheet; the download progress bar IS part-exposed and is
  // hidden in geske-model-viewer.css. Returns true once the style is in place.
  function hideDefaultPoster(mv) {
    var sr = mv.shadowRoot;
    if (!sr) return false;
    if (sr.querySelector('style[data-geske-mv]')) return true;
    var s = document.createElement('style');
    s.setAttribute('data-geske-mv', '');
    s.textContent = '#default-poster{display:none!important;background:transparent!important;}';
    sr.appendChild(s);
    return true;
  }

  function enhance(mv) {
    if (!mv || mv.dataset.geskeEnhanced === 'true') return;
    mv.dataset.geskeEnhanced = 'true';

    // The shadow root only exists once the <model-viewer> element is defined
    // and upgraded; retry after definition (and once more next frame) so the
    // square poster is hidden before the GLB finishes downloading.
    if (!hideDefaultPoster(mv) && window.customElements && customElements.whenDefined) {
      customElements.whenDefined('model-viewer').then(function () {
        if (!hideDefaultPoster(mv)) {
          requestAnimationFrame(function () { hideDefaultPoster(mv); });
        }
      });
    }

    // Auto framing so the model is guaranteed to fit. Once the GLB has
    // loaded we read its bounding box and tighten the radius so the device
    // fills roughly 80% of the panel height — see the 'load' handler below.
    if (!mv.hasAttribute('camera-orbit')) {
      mv.setAttribute('camera-orbit', '0deg 90deg auto');
    }
    // Lock the polar (vertical) angle to 90deg so the device can be spun
    // left/right but never tilted up or down. camera-orbit is "theta phi
    // radius": theta is the horizontal angle (left as auto so it stays free)
    // and phi is the vertical angle. Pinning phi's min and max to 90deg (the
    // equator / eye level) clamps out all vertical drag. Set as attributes
    // here so the lock holds even if the load-time radius pass below is
    // skipped; that pass keeps phi at 90deg and only narrows the radius.
    if (!mv.hasAttribute('min-camera-orbit')) {
      mv.setAttribute('min-camera-orbit', 'auto 90deg auto');
    }
    if (!mv.hasAttribute('max-camera-orbit')) {
      mv.setAttribute('max-camera-orbit', 'auto 90deg auto');
    }
    if (!mv.hasAttribute('field-of-view')) {
      mv.setAttribute('field-of-view', '30deg');
    }
    if (!mv.hasAttribute('min-field-of-view')) {
      mv.setAttribute('min-field-of-view', '15deg');
    }
    if (!mv.hasAttribute('max-field-of-view')) {
      mv.setAttribute('max-field-of-view', '45deg');
    }

    // Once the GLB has loaded, compute a tight radius from the actual
    // bounding box so the device fills ~80% of the gallery panel height
    // regardless of which product / scale the model was authored at, then
    // play the geske.com intro spin (see playIntroSpin below).
    var TARGET_FILL = 0.8; // device should occupy ~80% of viewport height
    var introRadius = null; // metres, resolved once the bounding box is known.

    // geske.com reveals the device with a single full turn around the
    // vertical axis the moment the model is ready: a GSAP tween of the
    // model's rotation.y by Math.PI * 2, eased expo.inOut over 1.7s, ending
    // back at the front. We reproduce that here by sweeping the camera's
    // azimuth (theta) a full 360deg over the same curve, which is visually
    // identical to the product spinning in place for a complete turn.
    var INTRO_DURATION = 1700; // ms — matches geske.com's gsap duration: 1.7.
    var INTRO_TURN = 360; // degrees; negate this to flip the spin direction.
    var prefersReducedMotion = !!(window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    // GSAP Expo.easeInOut, ported so the spin's slow/fast/slow cadence
    // matches geske.com rather than a generic ease.
    function easeInOutExpo(t) {
      if (t <= 0) return 0;
      if (t >= 1) return 1;
      return t < 0.5
        ? Math.pow(2, 20 * t - 10) / 2
        : (2 - Math.pow(2, -20 * t + 10)) / 2;
    }

    // Point the camera at a given azimuth, keeping phi pinned at 90deg (the
    // left/right-only lock) and the resolved fill radius. jumpCameraToGoal
    // snaps straight to the goal each frame so model-viewer's own
    // interpolation decay doesn't soften our eased curve.
    var orbitTo = function (thetaDeg) {
      mv.cameraOrbit = thetaDeg + 'deg 90deg ' +
        (introRadius != null ? introRadius + 'm' : 'auto');
      if (mv.jumpCameraToGoal) mv.jumpCameraToGoal();
    };

    var playIntroSpin = function () {
      // Honour reduced-motion: skip the animation, just rest at the front.
      if (prefersReducedMotion) { orbitTo(0); return; }
      var start = null;
      var step = function (now) {
        // Bail if the element was detached mid-spin (carousel slide change).
        if (!mv.isConnected) return;
        if (start === null) start = now;
        var t = Math.min((now - start) / INTRO_DURATION, 1);
        orbitTo(INTRO_TURN * easeInOutExpo(t));
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          orbitTo(0); // settle exactly at the front (360deg === 0deg).
        }
      };
      requestAnimationFrame(step);
    };

    var tighten = function () {
      try {
        var dims = mv.getDimensions ? mv.getDimensions() : null;
        if (dims) {
          // Use the longest silhouette dimension (max of width/height) so the
          // device still fits when the user drags to rotate. Depth (z) is
          // ignored because perspective changes during rotation.
          var silhouette = Math.max(dims.x, dims.y);
          var fovDeg = mv.getFieldOfView ? mv.getFieldOfView() : 30;
          var fovRad = fovDeg * Math.PI / 180;
          // radius such that the silhouette occupies TARGET_FILL of the
          // vertical viewport at the current field-of-view.
          introRadius = (silhouette / 2) / Math.tan((fovRad * TARGET_FILL) / 2);
          mv.cameraOrbit = '0deg 90deg ' + introRadius + 'm';
          // Keep phi pinned at 90deg here too: only the radius bounds change so
          // the device stays spinnable left/right but locked against up/down tilt.
          mv.minCameraOrbit = 'auto 90deg ' + (introRadius * 0.5) + 'm';
          mv.maxCameraOrbit = 'auto 90deg ' + (introRadius * 3) + 'm';
        }
      } catch (_) { /* no-op */ }
      // Spin whether or not the bounding box resolved; orbitTo falls back to
      // the auto radius when introRadius is still null.
      playIntroSpin();
    };
    if (mv.loaded) {
      tighten();
    } else {
      mv.addEventListener('load', tighten, { once: true });
    }

    // The PDP renders the model with reveal="interaction", so nothing loads
    // until activate() flips it to eager + auto. That happens once the page
    // has loaded and the main thread is idle, or straight away if the shopper
    // touches the gallery first. Loading the GLB, compiling shaders and the
    // intro spin used to land in the same window as the first taps on the
    // page, which is where the slow mobile INP came from (task 97).
    scheduleActivate(mv);
    // Hide the AR-only "Activate AR" overlay until we wire ar=true.
    mv.setAttribute('shadow-intensity', '1');
    // Disable user zoom (scroll wheel / pinch). The hero model is for rotating
    // the device, not zooming; wheel-zoom also hijacks page scroll. Orbit and
    // rotate stay enabled. model-viewer treats disable-zoom as a boolean attr.
    mv.setAttribute('disable-zoom', '');

    // Light the devices like geske.com: a warm studio environment for rich
    // brass and a high exposure so the near-black bodies show their surface
    // texture instead of crushing to flat black. NOTE: Shopify Horizon bundles
    // model-viewer v1.12, which hardcodes ACES tone-mapping (the `tone-mapping`
    // attribute is a no-op there) and crushes shadows hard — hence exposure 2.5,
    // tuned on v1.12 itself, not a newer build. Author-set attributes win.
    if (GESKE_ENV_URL && !mv.hasAttribute('environment-image')) mv.setAttribute('environment-image', GESKE_ENV_URL);
    if (!mv.hasAttribute('tone-mapping')) mv.setAttribute('tone-mapping', 'commerce');
    if (!mv.hasAttribute('exposure')) {
      var codeMatch = (mv.getAttribute('src') || '').match(/B0\d+/);
      var exp = (codeMatch && GESKE_EXPOSURE_BY_CODE[codeMatch[0]]) || GESKE_DEFAULT_EXPOSURE;
      mv.setAttribute('exposure', exp);
    }

    // Inject the branded poster slot. Skip if author has provided one.
    if (!mv.querySelector('[slot="poster"]')) {
      var poster = document.createElement('div');
      poster.setAttribute('slot', 'poster');
      poster.className = 'geske-model-poster';
      poster.innerHTML = POSTER_MARKUP;
      mv.appendChild(poster);
    }
  }

  // Eager-load + auto-reveal, so the 3D scene paints as soon as the GLB has
  // loaded. Idempotent.
  function activate(mv) {
    if (mv.dataset.geskeActivated === 'true') return;
    mv.dataset.geskeActivated = 'true';
    mv.setAttribute('loading', 'eager');
    mv.setAttribute('reveal', 'auto');
  }

  // Run fn once the page has loaded and the browser has an idle moment, with
  // a timeout so slow devices still get the model within a few seconds.
  var loadIdle = null;
  function afterLoadIdle(fn) {
    if (!loadIdle) {
      loadIdle = new Promise(function (resolve) {
        var idle = function () {
          if ('requestIdleCallback' in window) window.requestIdleCallback(resolve, { timeout: 2000 });
          else setTimeout(resolve, 200);
        };
        if (document.readyState === 'complete') idle();
        else window.addEventListener('load', idle, { once: true });
      });
    }
    loadIdle.then(fn);
  }

  function scheduleActivate(mv) {
    // Already revealed by an author attribute or a previous pass: leave it.
    if (mv.getAttribute('reveal') === 'auto' && mv.getAttribute('loading') === 'eager') {
      mv.dataset.geskeActivated = 'true';
      return;
    }
    // A tap anywhere in the gallery (or on its 3D thumbnail) means the
    // shopper wants the model now, so skip the wait.
    var scope = (mv.closest && mv.closest('.product-main__media, [data-gallery]')) || mv;
    var now = function () { activate(mv); };
    scope.addEventListener('pointerdown', now, { once: true, passive: true });
    var thumb = document.querySelector('.product-gallery__thumb--model');
    if (thumb) thumb.addEventListener('pointerdown', now, { once: true, passive: true });
    afterLoadIdle(now);
  }

  function scan(root) {
    (root || document).querySelectorAll('model-viewer').forEach(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Catch later-added model-viewers (deferred media activation,
  // carousel slide changes, zoom dialog template hydration).
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (!node || node.nodeType !== 1) continue;
        if (node.tagName && node.tagName.toLowerCase() === 'model-viewer') {
          enhance(node);
        } else if (node.querySelectorAll) {
          scan(node);
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
